import { useLazyQuery, useMutation } from '@apollo/client'
import { useTheme } from '@emotion/react'
import {
    Check,
    Close,
    Delete,
    Download,
    Edit,
    UploadFile,
    Visibility,
} from '@mui/icons-material'
import {
    Alert,
    AlertTitle,
    Box,
    FormControl,
    Grid,
    IconButton,
    InputLabel,
    Link,
    MenuItem,
    Select,
    Stack,
    TextField,
    Typography,
} from '@mui/material'
import {
    GetCandidateProfileForRecruiterQuery,
    GetCandidateProfileForRecruiterQueryVariables,
} from '__generated__/graphql'
import {
    deleteDocument,
    downloadDocument,
    renameCandidateDocument,
    updateDocumentType,
} from 'candidate/graphql/profile.gql'
import config from 'config'
import React, { Fragment, useCallback, useState } from 'react'
import ResumePreviewDialog, {
    isPdfDocument,
} from 'shared/components/ResumePreviewDialog'
import { ErrorCode, useDropzone } from 'react-dropzone'
import useToast from 'shared/components/Toast/useToast'
import useIsMobile from 'shared/hooks/useIsMobile'
import { DropdownOption } from 'shared/models/dropdownOption'
import { Separator } from 'shared/style/styled'
import { ApolloQueryRefetchFunction } from 'shared/types/graphql'
import { getPxAsRem, parsedDateAndMonth } from 'shared/utils'
import { LoadingPanel } from '../LoadingPanel'
import ConfirmDialog from './ConfirmDialog'

interface Props {
    loading?: boolean
    data: GetCandidateProfileForRecruiterQuery
    refetch: ApolloQueryRefetchFunction<
        GetCandidateProfileForRecruiterQuery,
        GetCandidateProfileForRecruiterQueryVariables
    >
    baseStyle: React.CSSProperties
    isRecruiterPortal?: boolean
}

const documentType = [
    { id: 'offerLetter', value: 'Offer Letter' },
    { id: 'resume', value: 'Resume' },
    { id: 'dealSheetTrackRecord', value: 'Deal Sheet/Track Record' },
    { id: 'review', value: 'Review' },
    { id: 'bio', value: 'Bio' },
    { id: 'other', value: 'Other' },
]

const DocumentUpload: React.FC<Props> = ({
    data,
    refetch,
    baseStyle,
    isRecruiterPortal,
}) => {
    const toast = useToast()
    const documents = data?.candidate?.documents
    const theme = useTheme()
    const isMobile = useIsMobile()
    const [open, setOpen] = useState<boolean>(false)
    const [uploadLoading, setUploadLoading] = useState<boolean>(false)
    const [documentId, setDocumentId] = useState<string>('')
    const [message, setMessage] = useState<string>('')
    const [title, setTitle] = useState<string>('')
    const [action, setAction] = useState<string>('')
    const [editingDocId, setEditingDocId] = useState<string | null>(null)
    const [editingName, setEditingName] = useState<string>('')
    const [editingExt, setEditingExt] = useState<string>('')
    const [deleteMutation] = useMutation(deleteDocument)
    const [updateTypeMutation] = useMutation(updateDocumentType)
    const [renameMutation] = useMutation(renameCandidateDocument)
    const [download] = useLazyQuery(downloadDocument, {
        fetchPolicy: 'network-only',
    })
    const [previewOpen, setPreviewOpen] = useState(false)
    const [previewUrl, setPreviewUrl] = useState<string | null>(null)
    const [previewFileName, setPreviewFileName] = useState<string | undefined>()
    const [previewLoading, setPreviewLoading] = useState(false)
    const uriPrefix = isRecruiterPortal
        ? `?candidateIdFromRecruiter=${data.candidate?.id}`
        : null

    const splitFileName = (fullName: string) => {
        const lastDot = fullName.lastIndexOf('.')
        if (lastDot === -1) return { name: fullName, ext: '' }
        return {
            name: fullName.substring(0, lastDot),
            ext: fullName.substring(lastDot + 1),
        }
    }

    const getSuggestedName = (originalName: string) => {
        const firstName =
            data?.candidate?.user?.nickname ?? data?.candidate?.user?.firstName
        const lastName = data?.candidate?.user?.lastName
        if (firstName && lastName) {
            return `${lastName}, ${firstName}`
        }
        return splitFileName(originalName).name
    }

    const uploadDocument = useCallback(
        async (file: File) => {
            setUploadLoading(true)
            const formData = new FormData()
            formData.append('document', file)
            formData.append('documentType', 'Resume')
            try {
                const response = await fetch(
                    `${config.api.uri}/candidateDocumentUpload${uriPrefix}`,
                    {
                        method: 'POST',
                        body: formData,
                        headers: {
                            contentType: 'multipart/form-data',
                            Authorization:
                                'Bearer ' + localStorage.getItem('token'),
                            boundary: '----WebKitFormBoundary7MA4YWxkTrZu0gW',
                        },
                    }
                )
                const responseData = await response.json()
                if (response.ok) {
                    console.log(
                        'Document uploaded successfully: ',
                        responseData
                    )
                    await refetch()
                    if (isRecruiterPortal && responseData?.id) {
                        const { ext } = splitFileName(file.name)
                        setEditingDocId(responseData.id)
                        setEditingName(getSuggestedName(file.name))
                        setEditingExt(ext)
                    }
                    toast({
                        title: 'Document Uploaded',
                        message: `${file.name} has been uploaded`,
                        severity: 'success',
                    })
                } else {
                    console.error('Failed to upload document: ', responseData)
                    toast({
                        title: 'Something went wrong',
                        message: 'Please try again',
                    })
                }
            } catch (error) {
                console.error('Failed to upload document', error)
                toast({
                    title: 'Something went wrong',
                    message: 'Please try again',
                })
            }
            setUploadLoading(false)
        },
        [refetch, toast, uriPrefix, isRecruiterPortal, data]
    )

    const onDrop = useCallback(
        (acceptedFiles) => {
            acceptedFiles.forEach((file: File) => {
                uploadDocument(file)
            })
        },
        [uploadDocument]
    )

    const accept = {
        'application/msword': ['.doc'],
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
            ['.docx'],
        'application/pdf': ['.pdf'],
    }
    const { fileRejections, getRootProps, getInputProps } = useDropzone({
        accept,
        maxSize: 10 * 1024 * 1024,
        onDrop,
        multiple: false,
    })

    const fileRejectionItems = fileRejections.map(({ file, errors }) => {
        const FileRejectionMessage = ({
            code,
            message,
        }: {
            code: string
            message: string
        }) => {
            switch (code) {
                case ErrorCode.FileInvalidType:
                    const acceptedTypes = Object.values(accept)
                        .flat()
                        .join(', ')
                    return <>File must be type: {acceptedTypes}</>
                case ErrorCode.FileTooLarge:
                    return <>File must be smaller than 10 MB</>
                case ErrorCode.FileTooSmall:
                case ErrorCode.TooManyFiles:
                    return <>{message}</>
            }
        }

        return (
            <Stack sx={{}}>
                <Alert severity="error">
                    <AlertTitle>Error</AlertTitle>
                    <ul>
                        {errors.map((e) => (
                            <>
                                <li key={e.code}>
                                    {file.name} -{' '}
                                    <FileRejectionMessage
                                        code={e.code}
                                        message={e.message}
                                    />
                                </li>
                            </>
                        ))}
                    </ul>
                </Alert>
            </Stack>
        )
    })

    const formatDocumentKey = (docKey: string) => {
        // remove the folder path and candidateId from the document key (if present, seed data doesn't have it)
        if (docKey.includes('/')) {
            const splitDocKey = docKey.split('/')
            return splitDocKey[splitDocKey.length - 1]
        } else {
            return docKey
        }
    }

    const handleDocumentUpdate = async (
        documentTypeSelection: DropdownOption | undefined,
        id: string
    ) => {
        if (documentTypeSelection?.value === 'Resume') {
            // open confirmation modal
            setOpen(true)
            setDocumentId(id)
            const documentChangeMessage = `
        If you mark this as a resume, it will replace the previous one.
        Make sure you have a copy of the old resume if you need to keep it.`
            setMessage(documentChangeMessage)
            setTitle('Warning')
            setAction('documentChange')
        } else {
            try {
                // autosave
                await updateTypeMutation({
                    variables: {
                        documentId: id,
                        documentType: documentTypeSelection?.value,
                    },
                })
                refetch()
            } catch (error) {
                console.log(error)
                toast({
                    title: 'Something went wrong',
                    message: 'Please try again later.',
                })
            }
        }
    }

    const handleCloseModal = () => {
        setOpen(false)
        setDocumentId('')
        setAction('')
        setTitle('')
        setMessage('')
    }

    const handleDeleteIconClick = (id: string) => {
        setDocumentId(id)
        setOpen(true)
        setMessage('This file will be permanently removed.')
        setTitle('Are you sure?')
        setAction('delete')
    }

    const handlePreviewClick = async (id: string, documentKey: string) => {
        setPreviewUrl(null)
        setPreviewLoading(true)
        setPreviewFileName(formatDocumentKey(documentKey))
        setPreviewOpen(true)
        try {
            const { data } = await download({
                variables: { documentId: id, forceInline: true },
            })
            setPreviewUrl(data?.download?.url ?? null)
        } catch {
            setPreviewUrl(null)
        }
        setPreviewLoading(false)
    }

    const handleDownloadClick = async (id: string) => {
        const { data } = await download({ variables: { documentId: id } })
        if (data.download.url) {
            const hiddenDownloadLink = document.createElement('a')
            hiddenDownloadLink.href = data.download.url
            hiddenDownloadLink.target = '_blank'
            hiddenDownloadLink.click()
            hiddenDownloadLink.remove()
        } else {
            toast({
                title: 'Something went wrong',
                message: 'Please try again later.',
            })
        }
    }

    const startEditing = (docId: string, currentKey: string) => {
        const fullName = formatDocumentKey(currentKey)
        const { name, ext } = splitFileName(fullName)
        setEditingDocId(docId)
        setEditingName(name)
        setEditingExt(ext)
    }

    const cancelEditing = () => {
        setEditingDocId(null)
        setEditingName('')
        setEditingExt('')
    }

    const confirmRename = async () => {
        if (!editingDocId || !editingName.trim()) return
        try {
            const result = await renameMutation({
                variables: {
                    documentId: editingDocId,
                    newFileName: editingName.trim(),
                },
            })
            if (!result.data?.renameCandidateDocument?.success) {
                toast({
                    title: 'Something went wrong',
                    message: 'Please try again later.',
                })
                return
            }
            await refetch()
            cancelEditing()
        } catch (error) {
            console.error(error)
            toast({
                title: 'Something went wrong',
                message: 'Please try again later.',
            })
        }
    }

    const handleConfirmModal = async () => {
        try {
            if (action === 'delete') {
                await deleteMutation({ variables: { documentId } })
            } else if (action === 'documentChange') {
                await updateTypeMutation({
                    variables: {
                        documentId,
                        documentType: 'Resume',
                    },
                })
            }
            refetch()
            setOpen(false)
            setDocumentId('')
            setAction('')
            setTitle('')
            setMessage('')
        } catch (error) {
            console.log(error)
            toast({
                title: 'Something went wrong',
                message: 'Please try again later.',
            })
        }
    }
    return (
        <Box sx={{ background: '#fff', padding: 2, borderRadius: 2 }}>
            <ConfirmDialog
                open={open}
                handleClose={handleCloseModal}
                handleConfirm={handleConfirmModal}
                message={message}
                title={title}
            />
            <ResumePreviewDialog
                open={previewOpen}
                onClose={() => setPreviewOpen(false)}
                url={previewUrl}
                fileName={previewFileName}
                loading={previewLoading}
            />
            <Box
                sx={{
                    ...baseStyle,
                }}
            >
                <div
                    {...getRootProps()}
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                    }}
                >
                    <input {...getInputProps()} />
                    <IconButton size="small">
                        <UploadFile color="primary" />
                    </IconButton>
                    <Typography sx={{ px: getPxAsRem(4) }}>
                        <Link underline="hover" color="primary">
                            Click to upload
                        </Link>
                        &nbsp; or drag and drop
                    </Typography>
                    <Typography>.doc, .docx, .pdf</Typography>
                </div>
            </Box>
            {fileRejectionItems.length > 0 && (
                <div style={{ margin: getPxAsRem(28) }}>
                    {fileRejectionItems}
                </div>
            )}
            {uploadLoading && <LoadingPanel />}
            {documents && documents.length > 0 ? (
                <Grid
                    container
                    sx={{
                        marginTop: getPxAsRem(20),
                        padding: getPxAsRem(36),
                    }}
                >
                    {documents?.map((doc, index) => (
                        <Fragment key={`doc_${doc.id}`}>
                            <Grid item xs={12}>
                                <Stack
                                    direction={isMobile ? 'column' : 'row'}
                                    justifyContent={
                                        isMobile ? 'center' : 'space-between'
                                    }
                                    alignItems={
                                        isMobile ? 'flex-start' : 'center'
                                    }
                                    rowGap={1}
                                >
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                        }}
                                    >
                                        <UploadFile
                                            color="primary"
                                            fontSize="small"
                                        />
                                        <Box sx={{ py: getPxAsRem(16) }}>
                                            {editingDocId === doc.id ? (
                                                <Stack
                                                    direction="row"
                                                    alignItems="center"
                                                    spacing={0.5}
                                                >
                                                    <TextField
                                                        size="small"
                                                        value={editingName}
                                                        onChange={(e) =>
                                                            setEditingName(
                                                                e.target.value
                                                            )
                                                        }
                                                        onKeyDown={(e) => {
                                                            if (
                                                                e.key ===
                                                                'Enter'
                                                            )
                                                                confirmRename()
                                                            if (
                                                                e.key ===
                                                                'Escape'
                                                            )
                                                                cancelEditing()
                                                        }}
                                                        autoFocus
                                                        sx={{ minWidth: 220 }}
                                                        InputProps={{
                                                            endAdornment:
                                                                editingExt ? (
                                                                    <Typography
                                                                        variant="body2"
                                                                        color="text.secondary"
                                                                        sx={{
                                                                            whiteSpace:
                                                                                'nowrap',
                                                                        }}
                                                                    >
                                                                        .
                                                                        {
                                                                            editingExt
                                                                        }
                                                                    </Typography>
                                                                ) : undefined,
                                                        }}
                                                    />
                                                    <IconButton
                                                        size="small"
                                                        onClick={confirmRename}
                                                        color="primary"
                                                    >
                                                        <Check fontSize="small" />
                                                    </IconButton>
                                                    <IconButton
                                                        size="small"
                                                        onClick={cancelEditing}
                                                    >
                                                        <Close fontSize="small" />
                                                    </IconButton>
                                                </Stack>
                                            ) : (
                                                <Stack
                                                    direction="row"
                                                    alignItems="center"
                                                    spacing={0.5}
                                                >
                                                    <Typography
                                                        sx={{
                                                            cursor: 'pointer',
                                                            '&:hover': {
                                                                textDecoration:
                                                                    'underline',
                                                            },
                                                        }}
                                                        onClick={() =>
                                                            startEditing(
                                                                doc.id,
                                                                doc.documentKey
                                                            )
                                                        }
                                                    >
                                                        {formatDocumentKey(
                                                            doc.documentKey
                                                        )}
                                                    </Typography>
                                                    <IconButton
                                                        size="small"
                                                        onClick={() =>
                                                            startEditing(
                                                                doc.id,
                                                                doc.documentKey
                                                            )
                                                        }
                                                    >
                                                        <Edit
                                                            sx={{
                                                                fontSize: 14,
                                                            }}
                                                        />
                                                    </IconButton>
                                                </Stack>
                                            )}
                                            <Box display={'flex'}>
                                                <Typography variant="body2">
                                                    {parsedDateAndMonth(
                                                        new Date(
                                                            doc.updatedAt ??
                                                                doc.createdAt
                                                        ).toLocaleString()
                                                    )}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </Box>
                                    <Stack
                                        direction={
                                            isMobile ? 'row-reverse' : 'row'
                                        }
                                        alignItems="center"
                                    >
                                        {isPdfDocument(doc.documentKey) && (
                                            <IconButton
                                                size="small"
                                                onClick={() =>
                                                    handlePreviewClick(
                                                        doc.id,
                                                        doc.documentKey
                                                    )
                                                }
                                                title="Preview"
                                            >
                                                <Visibility fontSize="small" />
                                            </IconButton>
                                        )}
                                        <Box
                                            sx={{
                                                padding: getPxAsRem(0, 16),
                                                alignItems: 'center',
                                                display: 'flex',
                                                flexShrink: 0,
                                                minWidth: '15.5rem',
                                            }}
                                        >
                                            <FormControl fullWidth>
                                                <InputLabel
                                                    id={`document_type${index}_label`}
                                                >
                                                    Choose Document Type
                                                </InputLabel>
                                                <Select
                                                    fullWidth
                                                    id={`document_type${index}`}
                                                    labelId={`document_type${index}_label`}
                                                    label={
                                                        'Choose Document Type'
                                                    }
                                                    onChange={(e) => {
                                                        const documentTypeSelection =
                                                            documentType.find(
                                                                ({ value }) =>
                                                                    value ===
                                                                    e.target
                                                                        .value
                                                            )
                                                        handleDocumentUpdate(
                                                            documentTypeSelection,
                                                            doc.id
                                                        )
                                                    }}
                                                    value={
                                                        doc.documentType ?? ''
                                                    }
                                                    disabled={false}
                                                    inputProps={{
                                                        sx: {
                                                            padding: `${getPxAsRem(8, 35, 8, 12)} !important`,
                                                            border: !doc.documentType
                                                                ? `1px solid ${theme.palette.error.main}`
                                                                : undefined,
                                                        },
                                                    }}
                                                >
                                                    {documentType.map(
                                                        (item) => (
                                                            <MenuItem
                                                                key={item.id}
                                                                value={
                                                                    item.value
                                                                }
                                                            >
                                                                {item.value}
                                                            </MenuItem>
                                                        )
                                                    )}
                                                </Select>
                                            </FormControl>
                                        </Box>
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                justifyContent: 'space-around',
                                                alignItems: 'center',
                                            }}
                                        >
                                            <IconButton
                                                size="small"
                                                onClick={() =>
                                                    handleDownloadClick(doc.id)
                                                }
                                            >
                                                <Download fontSize="small" />
                                            </IconButton>
                                            <IconButton
                                                size="small"
                                                onClick={() =>
                                                    handleDeleteIconClick(
                                                        doc.id
                                                    )
                                                }
                                            >
                                                <Delete fontSize="small" />
                                            </IconButton>
                                        </Box>
                                    </Stack>
                                </Stack>
                            </Grid>
                            <Grid item sm={12} xs={12}>
                                <Separator />
                            </Grid>
                        </Fragment>
                    ))}
                </Grid>
            ) : (
                ''
            )}
        </Box>
    )
}

export default DocumentUpload
